<script setup lang="ts" generic="RowDataType extends GridRawRowDataType">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import BasePagination from '@/common/components/molecules/basePagination/BasePagination.vue';
import FilterSearch from '@/common/components/molecules/filterSearch/FilterSearch.vue';
import ChipCellTooltipContent from '@/common/components/molecules/grid/ChipCellTooltipContent.vue';
import TagItem from '@/common/components/molecules/tag/TagItem.vue';
import BaseCustomTooltip from '@/common/components/molecules/baseCustomTooltip/BaseCustomTooltip.vue';
import { ClickedCellInfoForTransformed } from '@/common/utils';
import { capitalize, isArray, upperCase, isNil } from 'lodash-es';
import { convertMsToSec, numberWithComma } from '@/common/utils/commonUtils';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import BaseGridRatioBarCell from '@/common/components/molecules/BaseGridRatioBarCell.vue';
import BaseGridChipsCell from '@/common/components/molecules/baseGridChipsCell/BaseGridChipsCell.vue';
import ColorBallCell from '@/kubernetes/components/colorBallCell/colorBallCell.vue';
import DeltaSymbolCell from '@/common/components/molecules/deltaSymbolCell/DeltaSymbolCell.vue';
import TrendChartCell from '@/common/components/molecules/trendChartCell/TrendChartCell.vue';
import { GridColumnDef, GridRawRowDataType, GridRowDataType } from './modernGrid.types';
import ModernGridColumnSetting from './columnSetting/ModernGridColumnSetting.vue';
import { getHeaderRowSpan, resolveEmptyStateMessage } from './modernGrid.utils';
import {
  defaultPagination,
  defaultTokenFilter,
  defaultTextFilter,
  defaultSortingOption,
  defaultExpandingOption,
  defaultTreeViewOption,
  defaultDetailRowOption,
  defaultColumnResizingOption,
  defaultColumnOrderingOption,
  defaultColumnPinningOption,
  defaultStyleOption,
  defaultColumnVisibilityOption,
  defaultRowSelectionOption,
  defaultGridSettingOption,
  defaultRowMenuOption,
  defaultColumnAutoFitOption,
  defaultUseNumberColumn,
  defaultTooltipOption,
  defaultGroupingOption,
  CHECKBOX_COLUMN_ID,
  EXPAND_BTN_COLUMN_ID,
  NUMBER_COLUMN_ID,
  DEFAULT_HEADER_HEIGHT,
} from './modernGrid.define';
import QuickFilter from '../quickFilter/QuickFilter.vue';
import { Emits, Props, Slots, setup } from './modernGrid.setup';

const props = withDefaults(defineProps<Props<RowDataType>>(), {
  columns: () => [],
  rows: () => [],
  option: () => ({
    pagination: defaultPagination,
    filterSearch: defaultTokenFilter,
    textSearch: defaultTextFilter,
    sorting: defaultSortingOption,
    grouping: defaultGroupingOption,
    expanding: defaultExpandingOption,
    treeView: defaultTreeViewOption,
    detailRow: defaultDetailRowOption,
    columnResizing: defaultColumnResizingOption,
    columnOrdering: defaultColumnOrderingOption,
    columnPinning: defaultColumnPinningOption,
    columnVisibility: defaultColumnVisibilityOption,
    gridSetting: defaultGridSettingOption,
    rowSelection: defaultRowSelectionOption,
    style: defaultStyleOption,
    rowMenu: defaultRowMenuOption,
    autoFit: defaultColumnAutoFitOption,
    useNumberColumn: defaultUseNumberColumn,
    tooltipOption: defaultTooltipOption,
  }),
  highlightedRows: () => [],
  disabledRows: () => [],
  uncheckableRows: () => [],
  dynamicStyles: () => [],
  expandedRows: () => [],
  checkedRows: () => [],
  customClass: () => ({}),
  emptyMessage: undefined,
  searchEmptyMessage: undefined,
  initialSearchWord: '',
});
const emit = defineEmits<Emits<RowDataType>>();
const slots = defineSlots<Slots<RowDataType>>();

const {
  showToolbar,
  t,
  gridRef,
  summaryRef,
  resizeHandleWidth,
  resizeHandleHeight,
  containerWidth,
  gridWrapperClasses,
  onContainerScroll,
  table,
  tableRows,
  headerDepth,
  styleInfo,
  chipTooltipOptions,
  getCellColumnMeta,
  getHeaderColumnMeta,
  getCellRendererType,
  isCellClickable,
  getByteCellTitle,
  getFormattedByteValue,
  getToSecCellTitle,
  getToSecCellValue,
  getDigitCellTitle,
  getDigitCellValue,
  getUseHeaderCellOrderingFromHeader,
  getIsVisibleOrderingHandleFromHeader,
  getSortingBtnIconFromHeader,
  onClickHeaderCellFromHeader,
  syncGridState,
  resetSort,

  totalRowsHeight,
  virtualRowManager,
  virtualRows,
  scrollToHighlighted,

  gridSettingMenuRef,
  gridSettingMenuItems,
  columnSettingRef,
  gridSettingInfo,
  columnSettingList,
  onClickSettingBtn,
  exportExcel,
  updateColumnSizingState,

  filterSearchRef,
  textFilterInfo,
  tokenFilterInfo,
  filterSearchResultMV,
  quickFilterResultMV,
  searchWord,
  autoValueItems,
  clearFilterSearchToken,
  clearFilterSearchResultMV,

  columnResizingInfo,
  columnOrderingInfo,
  columnSizingInfoState,
  resizeHandleMirrorXPosition,
  cloneHeight,
  resizeColumn,
  isVisibleResizeHandle,
  isDragging,
  isOverNonDroppable,
  columnMenuRef,
  columnMenuItems,
  setColumnRef,
  getHeaderCellAttributes,
  getHeaderCellStyle,
  getHeaderCellClasses,
  getCellHeight,
  getClickableHeader,
  headerExpandBtnIcon,

  treeViewInfo,
  expandingInfo,
  detailRowInfo,
  expandingColumn,
  rowSelectionInfo,
  selectRow,
  onClickRow,
  setRowSelectionState,
  isRowUncheckable,
  isAllRowsUncheckable,
  toggleAllSelectableRows,
  toggleRowCheckbox,
  isAllSelectableRowsSelected,
  isSomeSelectableRowsSelected,

  getBodyRowAttributes,
  getBodyRowDynamicStyle,
  getBodyRowVirtualStyle,
  getBodyRowClasses,
  getBodyCellStyle,
  getBodyCellClasses,
  getBodyCellPaddingLeft,
  hasExpandableRows,
  getBodyExpandBtnIcon,
  getCellValue,
  getIsDefaultCell,
  getRenderingValue,
  tooltipItems,
  customTooltipRef,
  currentTooltipFieldName,
  currentTooltipRowData,
  currentTooltipColumnIndex,
  isChipCellClickable,
  clickCount,
  clickChip,
  hideTooltip,
  clickCountWithFieldData,
  onClickTooltipItem,
  clickCell,

  paginationInfo,
  currentPageNum,
  perDataCnt,
  totalRowCount,

  rowMenuInfo,
  rowMenuRef,
  rowMenuItems,
  onClickRowMenuBtn,

  hasSummaryRow,
  getSummaryDisplayValue,
  getSummaryCellAlignClass,
  getSummaryCellStyle,
} = setup<RowDataType>(props, emit, slots);

defineExpose({
  table,
  syncGridState,
  syncRowSelection: setRowSelectionState,
  exportExcel,
  searchWord,
  clearFilterSearchToken,
  clearFilterSearchResult: clearFilterSearchResultMV,
  resetSort,
  scrollToHighlighted,
});
</script>

<template>
  <div
    class="modern-grid"
    :style="{
      width: typeof styleInfo.width === 'string' ? styleInfo.width : `${styleInfo.width}px`,
      height: typeof styleInfo.height === 'string' ? styleInfo.height : `${styleInfo.height}px`,
    }"
  >
    <!-- sub toolbar slot -->
    <div
      v-if="$slots.topToolbar"
      class="modern-grid-top-toolbar"
    >
      <slot name="topToolbar" />
    </div>
    <!-- toolbar -->
    <div
      v-if="showToolbar"
      class="modern-grid-toolbar"
    >
      <div class="modern-grid-toolbar-container">
        <!-- toolbarPrefix - 검색 필드 앞에 추가되는 슬롯 -->
        <slot name="toolbarPrefix" />
        <div
          v-if="tokenFilterInfo.use || textFilterInfo.use"
          class="modern-grid-toolbar-container--left"
        >
          <div
            v-if="tokenFilterInfo.use"
            :class="['modern-grid-toolbar__row', 'search', tokenFilterInfo.mode]"
          >
            <filter-search
              ref="filterSearchRef"
              v-model:selected-values="filterSearchResultMV"
              :filter-items="tokenFilterInfo.filterItems ?? []"
              :storage-key="tokenFilterInfo.storageKey ?? 'modern-grid'"
              :auto-value-items="autoValueItems"
              :use-dashboard-store="props.useDashboardStore ?? false"
              :default-active="tokenFilterInfo.defaultActive"
              :persist-active-state="tokenFilterInfo.persistActiveState"
            />
          </div>
          <div
            v-if="textFilterInfo.use && !textFilterInfo.hideInput"
            :class="['modern-grid-toolbar__row', 'search', textFilterInfo.mode]"
          >
            <ev-text-field
              v-model="searchWord"
              :placeholder="textFilterInfo.placeholder || t('WORD.SEARCH')"
              :max-length="textFilterInfo.maxLength"
              :clearable="textFilterInfo.clearable"
            >
              <template #icon-prefix>
                <ev-icon icon="ev-icon-search2" />
              </template>
            </ev-text-field>
          </div>
        </div>
        <!-- toolbarPostfix - 검색 필드 뒤에 추가되는 슬롯 -->
        <slot name="toolbarPostfix" />
        <div
          :class="[
            'modern-grid-toolbar-container--right',
            { 'width-100': !tokenFilterInfo.use && !textFilterInfo.use && $slots.toolbar },
          ]"
        >
          <!-- toolbar - custom slot -->
          <slot name="toolbar" />
          <base-button
            v-if="gridSettingInfo.use"
            size="medium"
            class="modern-grid__setting-btn"
            type="icon"
            @click="onClickSettingBtn($event)"
          >
            <ev-icon
              size="medium"
              icon="icon-more-vertical"
            />
          </base-button>
        </div>
      </div>
      <div
        v-if="tokenFilterInfo?.quickFilter?.use && tokenFilterInfo.quickFilter.elements.length"
        class="modern-grid-toolbar__quick-filter"
      >
        <quick-filter
          v-model:quick-filter-result="quickFilterResultMV"
          :quick-filter-list="tokenFilterInfo.quickFilter.elements"
        />
      </div>
    </div>

    <!-- sub toolbar slot -->
    <div
      v-if="$slots.subToolbar"
      class="modern-grid-sub-toolbar"
    >
      <slot name="subToolbar" />
    </div>

    <!--  table  -->
    <div :class="gridWrapperClasses">
      <div
        ref="gridRef"
        :class="['modern-grid__container', { 'no-header': !styleInfo.header?.show }]"
        @scroll="onContainerScroll"
      >
        <table
          :class="[
            'modern-grid-table',
            {
              'is-dragging': isDragging,
              'is-over-non-droppable': isOverNonDroppable,
              'is-sticky-header': styleInfo.header?.useSticky,
            },
          ]"
        >
          <!-- table 내부에 배치: 스크롤 시 리사이징 안내선이 header + body 전체 높이를 커버하기 위함 (table은 display: grid로 렌더링됨) -->
          <div
            v-if="columnResizingInfo.use"
            :class="[
              'modern-grid-header__resize-handle-mirror',
              { 'is-resizing': !!columnSizingInfoState.isResizingColumn },
            ]"
            :style="{ transform: `translateX(${resizeHandleMirrorXPosition})` }"
          />
          <thead
            v-show="styleInfo.header?.show"
            :class="[
              'modern-grid-header',
              {
                'show-column-line': styleInfo.header?.showColumnLine,
                'show-row-line': styleInfo.header?.showRowLine,
              },
            ]"
          >
            <tr
              v-if="!table?.getVisibleFlatColumns().length"
              class="modern-grid-header__row"
            >
              <th
                :style="{
                  height: `${styleInfo.header?.height || styleInfo.body?.rowHeight}px`,
                }"
                class="modern-grid-header__cell modern-grid-header__cell--empty"
              />
            </tr>
            <tr
              v-for="(headerGroup, headerIndex) in table?.getHeaderGroups()"
              v-else
              :key="headerGroup.id"
              :ref="(el) => setColumnRef(el, headerIndex)"
              class="modern-grid-header__row"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="`${headerGroup.id}_${header.id}`"
                :colspan="header.colSpan"
                :rowspan="getHeaderRowSpan(header)"
                v-bind="getHeaderCellAttributes(header)"
                :style="getHeaderCellStyle(header, headerGroup.headers)"
                :class="getHeaderCellClasses(header)"
              >
                <div
                  :class="[
                    'modern-grid-header__cell-content',
                    {
                      'modern-grid-header__ordering-handle':
                        getUseHeaderCellOrderingFromHeader(header),
                    },
                  ]"
                  :style="{ height: `${getCellHeight(header)}px` }"
                >
                  <template v-if="!!getHeaderColumnMeta(header).customHeader && slots.customHeader">
                    <div
                      v-if="
                        !!getHeaderColumnMeta(header).customHeaderSort && header.column.getCanSort()
                      "
                      class="modern-grid-header__custom-header-sort"
                    >
                      <slot
                        name="customHeader"
                        :item="{ header }"
                      />
                      <base-button
                        type="icon"
                        size="small"
                        :class="['modern-grid-header__sorting-btn', header.column.getIsSorted()]"
                        @click.stop="header.column.getToggleSortingHandler()?.($event)"
                      >
                        <ev-icon
                          size="small"
                          class="modern-grid-header__sorting-btn__icon"
                          :icon="getSortingBtnIconFromHeader(header)"
                        />
                      </base-button>
                    </div>
                    <slot
                      v-else
                      name="customHeader"
                      :item="{ header }"
                    />
                  </template>

                  <template
                    v-else-if="
                      header.column.id === CHECKBOX_COLUMN_ID &&
                      rowSelectionInfo.useCheckAll &&
                      rowSelectionInfo.mode !== 'single'
                    "
                  >
                    <ev-checkbox
                      :indeterminate="isSomeSelectableRowsSelected"
                      :model-value="isAllSelectableRowsSelected"
                      :disabled="!tableRows.length || isAllRowsUncheckable"
                      @change="toggleAllSelectableRows"
                    />
                  </template>

                  <template
                    v-else-if="
                      header.column.id === EXPAND_BTN_COLUMN_ID && expandingInfo.useHeaderExpanding
                    "
                  >
                    <base-button
                      type="icon"
                      size="medium"
                      class="modern-grid-header__expand-btn"
                      @click.stop="() => table?.toggleAllRowsExpanded()"
                    >
                      <ev-icon
                        size="medium"
                        :icon="headerExpandBtnIcon"
                      />
                    </base-button>
                  </template>

                  <template v-else-if="!header.isPlaceholder">
                    <base-button
                      v-if="getIsVisibleOrderingHandleFromHeader(header)"
                      type="icon"
                      size="small"
                      class="modern-grid-header__ordering-handle"
                    >
                      <ev-icon
                        size="small"
                        :icon="columnOrderingInfo.handleIcon ?? 'ev-icon-hamburger2'"
                      />
                    </base-button>
                    <base-button
                      v-if="
                        !!expandingInfo.useHeaderExpanding && expandingColumn === header.column.id
                      "
                      type="icon"
                      size="medium"
                      class="modern-grid-header__expand-btn"
                      @click.stop="() => table?.toggleAllRowsExpanded()"
                    >
                      <ev-icon
                        size="medium"
                        :icon="headerExpandBtnIcon"
                      />
                    </base-button>
                    <span
                      class="modern-grid-header__cell-value__wrapper"
                      :class="{
                        'modern-grid-header__cell-value__wrapper--sortable':
                          getClickableHeader(header),
                      }"
                      @click.stop="onClickHeaderCellFromHeader({ header, e: $event })"
                    >
                      <span
                        :title="header.column.columnDef.header as string"
                        :class="['modern-grid-header__cell-value', 'column-name']"
                      >
                        {{ header.column.columnDef.header }}
                      </span>
                      <base-button
                        v-if="
                          header.column.getCanSort() && !getHeaderColumnMeta(header).customHeader
                        "
                        type="icon"
                        size="small"
                        :class="['modern-grid-header__sorting-btn', header.column.getIsSorted()]"
                        @click.stop="header.column.getToggleSortingHandler()?.($event)"
                      >
                        <ev-icon
                          size="small"
                          class="modern-grid-header__sorting-btn__icon"
                          :icon="getSortingBtnIconFromHeader(header)"
                        />
                      </base-button>
                    </span>
                  </template>
                </div>
                <div
                  v-if="isVisibleResizeHandle(header)"
                  class="modern-grid-header__resize-handle"
                  @dblclick="columnResizingInfo.enableReset ? header.column.resetSize() : null"
                  @mousedown.stop="resizeColumn(header, table, $event)"
                />
              </th>
            </tr>
          </thead>
          <tbody
            :class="[
              'modern-grid-body',
              {
                'show-column-line': styleInfo.body?.showColumnLine,
                'show-row-line': styleInfo.body?.showRowLine,
                'is-striped': styleInfo.body?.useStripe,
              },
            ]"
            :style="{
              height: `${totalRowsHeight}px`,
            }"
          >
            <template v-if="!tableRows.length">
              <tr class="modern-grid-body__row modern-grid-body__row--empty">
                <td
                  :colSpan="table?.getAllLeafColumns().length"
                  class="modern-grid-body__cell modern-grid-body__cell--empty center-align"
                >
                  <div class="modern-grid-body__cell-content">
                    {{
                      resolveEmptyStateMessage({
                        hasOriginRows: !!props.rows.length,
                        emptyMessage: props.emptyMessage,
                        searchEmptyMessage: props.searchEmptyMessage,
                        noDataText: t('MESSAGE.NO_DATA'),
                        noRecordsText: t('MESSAGE.NO_RECORDS'),
                      })
                    }}
                  </div>
                </td>
              </tr>
            </template>
            <template
              v-for="virtualRow in virtualRows"
              v-else
              :key="tableRows[virtualRow.index].id"
            >
              <tr
                :ref="(el) => virtualRowManager.measureElement(el as Element)"
                v-bind="getBodyRowAttributes(tableRows[virtualRow.index], virtualRow.index)"
                :class="getBodyRowClasses(tableRows[virtualRow.index])"
                :style="
                  getBodyRowVirtualStyle(
                    tableRows[virtualRow.index],
                    virtualRow,
                    !!slots?.rowDetail,
                  )
                "
              >
                <!-- detail row -->
                <template
                  v-if="
                    detailRowInfo.use &&
                    (tableRows[virtualRow.index].original.isDetail ??
                      tableRows[virtualRow.index].depth > 0) &&
                    !!slots?.rowDetail &&
                    tableRows[virtualRow.index].getVisibleCells().length > 0
                  "
                >
                  <td
                    :colSpan="
                      rowSelectionInfo.useCheckbox
                        ? tableRows[virtualRow.index].getAllCells().length + 2
                        : tableRows[virtualRow.index].getAllCells().length + 1
                    "
                    :class="[
                      'modern-grid-body__cell--detail',
                      getBodyCellClasses(tableRows[virtualRow.index].getVisibleCells()[0]),
                    ]"
                    @click.stop="
                      onClickRow(
                        {
                          event: $event,
                          rowData: tableRows[virtualRow.index].original,
                          rowIndex: virtualRow.index,
                          cellIndex: '0',
                          cellName: 'detail',
                          rowId: tableRows[virtualRow.index].id,
                        },
                        !tableRows[virtualRow.index].getCanSelect(),
                      )
                    "
                    @dblclick="
                      emit('dblclick-row', {
                        event: $event,
                        rowData: tableRows[virtualRow.index].original,
                        rowIndex: virtualRow.index,
                        cellIndex: '0',
                        cellName: 'detail',
                        bgColor: getBodyRowDynamicStyle(tableRows[virtualRow.index])
                          .backgroundColor,
                      })
                    "
                  >
                    <div class="modern-grid-body__cell-content">
                      <slot
                        name="rowDetail"
                        :item="{ row: tableRows[virtualRow.index].original }"
                      />
                    </div>
                  </td>
                </template>
                <template v-else>
                  <td
                    v-for="cell in tableRows[virtualRow.index].getVisibleCells()"
                    :key="`${cell.row.id}_${cell.id}`"
                    :style="getBodyCellStyle(cell, cell.row.getVisibleCells())"
                    :class="getBodyCellClasses(cell)"
                    class="clickoutside-ignore"
                    @click.stop="
                      onClickRow(
                        {
                          event: $event,
                          rowData: cell.row.original,
                          rowIndex: virtualRow.index,
                          cellIndex: `${cell.column.getIndex()}`,
                          cellName: cell.column.id,
                          rowId: cell.row.id,
                        },
                        !cell.row.getCanSelect(),
                      )
                    "
                    @dblclick="
                      emit('dblclick-row', {
                        event: $event,
                        rowData: cell.row.original,
                        rowIndex: virtualRow.index,
                        cellIndex: `${cell.column.getIndex()}`,
                        cellName: cell.column.id,
                        bgColor: getBodyRowDynamicStyle(tableRows[virtualRow.index])
                          .backgroundColor,
                      })
                    "
                  >
                    <div
                      :class="[
                        'modern-grid-body__cell-content',
                        {
                          'modern-grid-body__cell-content--tree-default':
                            treeViewInfo.use &&
                            expandingColumn === cell.column.id &&
                            !cell.row.getCanExpand() &&
                            cell.row.depth === 0,
                          'modern-grid-body__cell-content--tree-child':
                            treeViewInfo.use &&
                            expandingColumn === cell.column.id &&
                            cell.row.depth > 0,
                          'modern-grid-body__cell-content--tree-parent':
                            treeViewInfo.use &&
                            expandingColumn === cell.column.id &&
                            cell.row.getCanExpand() &&
                            cell.row.depth === 0,
                        },
                      ]"
                      :style="{
                        paddingLeft: getBodyCellPaddingLeft(cell),
                      }"
                    >
                      <template v-if="cell.column.id === NUMBER_COLUMN_ID">
                        <span
                          :title="`${virtualRow.index + 1}`"
                          class="modern-grid-body__cell-value"
                        >
                          {{ virtualRow.index + 1 }}
                        </span>
                      </template>

                      <template v-if="cell.column.id === CHECKBOX_COLUMN_ID">
                        <ev-checkbox
                          :model-value="
                            rowSelectionInfo.mode === 'each' ||
                            rowSelectionInfo.mode === 'parentOnly' ||
                            (detailRowInfo.use &&
                              cell.row.subRows?.some(
                                (subRow) => subRow.original?.isDetail === true,
                              ))
                              ? cell.row.getIsSelected()
                              : cell.row.getIsSelected() || cell.row.getIsAllSubRowsSelected()
                          "
                          :indeterminate="
                            rowSelectionInfo.mode === 'each' ||
                            rowSelectionInfo.mode === 'parentOnly' ||
                            (detailRowInfo.use &&
                              cell.row.subRows?.some(
                                (subRow) => subRow.original?.isDetail === true,
                              ))
                              ? false
                              : cell.row.getIsSomeSelected()
                          "
                          :disabled="!cell.row.getCanSelect() || isRowUncheckable(cell.row)"
                          @change="(_checked: boolean, e: Event) => toggleRowCheckbox(cell.row, e)"
                        />
                      </template>

                      <template v-else-if="cell.column.id === EXPAND_BTN_COLUMN_ID">
                        <base-button
                          v-if="!detailRowInfo.hideExpandBtnIfEmpty || cell.row.getCanExpand()"
                          size="medium"
                          type="icon"
                          class="modern-grid-body__expand-btn"
                          @click.stop="() => cell.row.toggleExpanded()"
                        >
                          <ev-icon
                            :size="expandingInfo.iconSize ?? 'medium'"
                            :icon="getBodyExpandBtnIcon(cell.row)"
                          />
                        </base-button>
                      </template>

                      <template v-else>
                        <span
                          v-if="
                            expandingColumn === cell.column.id &&
                            treeViewInfo.use &&
                            hasExpandableRows
                          "
                          class="modern-grid-body__expand-area"
                          @click.stop
                        >
                          <base-button
                            v-if="expandingInfo.expandBtnAlways || cell.row.getCanExpand()"
                            size="medium"
                            type="icon"
                            class="modern-grid-body__expand-btn"
                            @click.stop="() => cell.row.toggleExpanded()"
                          >
                            <ev-icon
                              size="medium"
                              :icon="getBodyExpandBtnIcon(cell.row)"
                            />
                          </base-button>
                        </span>

                        <!-- custom-renderer -->
                        <template v-if="!!slots[cell.column.id]">
                          <slot
                            :name="cell.column.id"
                            :item="{
                              row: cell.row.original,
                              rowIndex: cell.row.index,
                              column: cell.column.columnDef as GridColumnDef<RowDataType>,
                              cell: getCellValue(cell),
                              table,
                              depth: cell.row.depth,
                            }"
                          />
                        </template>

                        <!-- cell renders by rendererType -->
                        <base-grid-ratio-bar-cell
                          v-else-if="
                            getCellRendererType(cell) === 'ratio' &&
                            !isNil(cell.row.original[cell.column.id])
                          "
                          :ratio="cell.row.original[cell.column.id]"
                        />
                        <base-grid-chips-cell
                          v-else-if="
                            getCellRendererType(cell) === 'chip-cell' && isArray(cell.getValue())
                          "
                          :row-index="virtualRow.index"
                          :chips="getCellValue(cell) || []"
                          :shown-chip-count="props.option?.shownChipCount"
                          @click-count="clickCount"
                          @resize="hideTooltip"
                        />
                        <base-grid-chips-cell
                          v-else-if="
                            getCellRendererType(cell) === 'chip-cell-clickable' &&
                            isArray(cell.getValue())
                          "
                          :row-index="virtualRow.index"
                          :chips="getCellValue(cell) || []"
                          :shown-chip-count="props.option?.shownChipCount"
                          :field-name="cell.column.id as string"
                          :row-data="cell.row.original"
                          :column-index="cell.column.getIndex()"
                          :is-clickable="true"
                          @click-count-clickable="
                            (v) =>
                              clickCountWithFieldData({
                                ...v,
                                isClickable: getCellRendererType(cell) === 'chip-cell-clickable',
                              })
                          "
                          @resize="hideTooltip"
                          @click="
                            (event) =>
                              clickCell({
                                event,
                                field: cell.column.id,
                                value: getCellValue(cell),
                                row: cell.row.original,
                                cellIndex: cell.column.getIndex(),
                              })
                          "
                        />
                        <base-grid-chips-cell
                          v-else-if="getCellRendererType(cell) === 'multi-chip-cell'"
                          :row-index="virtualRow.index"
                          :chips="getCellValue(cell) || {}"
                          is-multiple-value-chip
                          @click-chip="clickChip"
                          @click-count="clickCount"
                          @resize="hideTooltip"
                        />
                        <base-grid-chips-cell
                          v-else-if="
                            getCellRendererType(cell) === 'use-html-chip-cell' &&
                            isArray(getCellValue(cell)) &&
                            getCellValue(cell).length > 0
                          "
                          :row-index="virtualRow.index"
                          :chips="getCellValue(cell)"
                          use-html
                          @click-count="clickCount"
                          @resize="hideTooltip"
                        />
                        <div
                          v-else-if="
                            getCellRendererType(cell) === 'color-ball-cell' &&
                            isArray(cell.getValue()) &&
                            getCellValue(cell).length > 0
                          "
                          :style="{ width: '100%' }"
                        >
                          <color-ball-cell
                            :field-name="cell.column.id as string"
                            :row="cell.row.original"
                            :cells="getCellValue(cell)"
                            @click-cell="
                              (value, event) =>
                                clickCell({
                                  event,
                                  field: cell.column.id,
                                  value: getCellValue(cell),
                                  row: cell.row.original,
                                  cellIndex: cell.column.getIndex(),
                                })
                            "
                          />
                        </div>
                        <div
                          v-else-if="getCellRendererType(cell) === 'label' && getCellValue(cell)"
                          class="label-cell-wrapper"
                        >
                          <status-tag
                            v-for="(status, index) in getCellValue(cell)?.split(', ').sort()"
                            :key="index"
                            :type="status"
                            :data-type="status"
                          >
                            {{ status }}
                          </status-tag>
                        </div>
                        <div
                          v-else-if="
                            getCellRendererType(cell) === 'capitalize-label' && getCellValue(cell)
                          "
                          class="label-cell-wrapper"
                        >
                          <status-tag
                            v-for="(status, index) in getCellValue(cell).split(', ').sort()"
                            :key="index"
                            :type="status"
                          >
                            {{ capitalize(status) }}
                          </status-tag>
                        </div>
                        <div
                          v-else-if="
                            getCellRendererType(cell) === 'upper-label' && getCellValue(cell)
                          "
                          class="label-cell-wrapper"
                        >
                          <status-tag
                            v-for="(status, index) in getCellValue(cell).split(', ').sort()"
                            :key="index"
                            :type="status"
                          >
                            {{ upperCase(status) }}
                          </status-tag>
                        </div>
                        <span
                          v-else-if="getCellRendererType(cell) === 'sec'"
                          :title="`${numberWithComma(getCellValue(cell))} ms`"
                          class="modern-grid-body__cell-value"
                        >
                          {{ convertMsToSec(getCellValue(cell)) }}
                        </span>
                        <span
                          v-else-if="getCellRendererType(cell) === 'toSec'"
                          :title="getToSecCellTitle(cell, getCellValue(cell))"
                          class="modern-grid-body__cell-value"
                        >
                          {{ getToSecCellValue(cell, getCellValue(cell)) }}
                        </span>
                        <span
                          v-else-if="getCellRendererType(cell) === 'byte'"
                          :title="getByteCellTitle(cell, getCellValue(cell))"
                          class="modern-grid-body__cell-value"
                        >
                          {{ getFormattedByteValue(cell, getCellValue(cell)) }}
                        </span>
                        <span
                          v-else-if="getCellRendererType(cell) === 'digit'"
                          :title="getDigitCellTitle(cell, getCellValue(cell))"
                          class="modern-grid-body__cell-value"
                        >
                          {{ getDigitCellValue(cell, getCellValue(cell)) }}
                        </span>
                        <delta-symbol-cell
                          v-else-if="getCellRendererType(cell) === 'deltaSymbolNumber'"
                          :value="getCellValue(cell)"
                          :decimal="getCellColumnMeta(cell).decimal"
                        />
                        <trend-chart-cell
                          v-else-if="
                            getCellRendererType(cell) === 'trend-chart-cell' &&
                            getCellValue(cell) &&
                            typeof getCellValue(cell) === 'object'
                          "
                          :data="getCellValue(cell)"
                          :series-options="{
                            name: cell.column.columnDef.header as string,
                          }"
                          :unit="getCellColumnMeta(cell).attrs?.unit"
                          :tooltip-description="getCellColumnMeta(cell).attrs?.tooltipDescription"
                        />
                        <div
                          v-else-if="isCellClickable(cell)"
                          class="modern-grid-body__cell--link"
                          :title="getCellValue(cell)"
                          @click.stop="
                            (event) => {
                              clickCell({
                                event,
                                field: cell.column.id,
                                value: getCellValue(cell),
                                row: cell.row.original,
                                cellIndex: cell.column.getIndex(),
                                cellName: cell.column.id,
                                rowIndex: virtualRow.index,
                                rowId: cell.row.id,
                              });
                              if (!rowSelectionInfo.disableCellClickSelect) {
                                selectRow(cell.row.id);
                              }
                            }
                          "
                        >
                          <span
                            v-if="!!(getCellValue(cell) || getCellValue(cell) === 0)"
                            class="modern-grid-body__cell-value"
                            :title="getCellValue(cell)"
                          >
                            {{ getCellValue(cell) }}
                          </span>
                        </div>
                        <!-- default cell value -->
                        <span
                          v-else-if="getIsDefaultCell(cell)"
                          :title="getCellValue(cell)"
                          class="modern-grid-body__cell-value"
                        >
                          {{ getRenderingValue(cell) }}
                        </span>
                      </template>
                    </div>
                  </td>
                </template>
                <td
                  v-if="rowMenuInfo.use"
                  class="modern-grid-body__cell--row-menu"
                >
                  <base-button
                    type="icon"
                    size="medium"
                    class="modern-grid-body__row-menu-btn"
                    @click.stop="(e) => onClickRowMenuBtn({ row: tableRows[virtualRow.index], e })"
                  >
                    <ev-icon
                      size="medium"
                      icon="icon-more-vertical"
                    />
                  </base-button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <!-- body 영역 전체를 차지하는 레이어 (NoDataTableBody 텔레포트 대상) -->
        <div
          class="modern-grid-body-area"
          :style="{
            top: `${styleInfo.header?.height ?? DEFAULT_HEADER_HEIGHT}px`,
            height: `calc(100% - ${styleInfo.header?.height ?? DEFAULT_HEADER_HEIGHT}px)`,
          }"
        />
      </div>
      <!-- Summary Footer -->
      <div
        v-if="hasSummaryRow"
        ref="summaryRef"
        class="modern-grid-summary"
      >
        <div class="modern-grid-summary__row">
          <div
            v-for="column in table?.getVisibleLeafColumns()"
            :key="`summary-${column.id}`"
            :class="['modern-grid-summary__cell', getSummaryCellAlignClass(column.id)]"
            :style="getSummaryCellStyle(column.id)"
          >
            <div class="modern-grid-summary__cell-content">
              {{ getSummaryDisplayValue(column.id) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--  pagination  -->
    <div
      v-if="paginationInfo.use"
      class="modern-grid__pagination"
    >
      <base-pagination
        v-model:current="currentPageNum"
        v-model:per-data="perDataCnt"
        :items="paginationInfo.items"
        :show-setting="paginationInfo.showSetting"
        :show-page-info="paginationInfo.showPageInfo"
        :total="totalRowCount"
      />
    </div>

    <!--  grid setting menu  -->
    <ev-context-menu
      ref="gridSettingMenuRef"
      custom-class="modern-grid__setting-menu"
      :items="gridSettingMenuItems"
    />

    <modern-grid-column-setting
      ref="columnSettingRef"
      :column-setting-list="columnSettingList"
      @change-column-visibility="updateColumnSizingState"
    />

    <!--  column menu (8620 작업에서 컬럼 메뉴 사용하지 않도록 수정, 추후 다시 사용될 수 있으므로 코드는 삭제 X)  -->
    <ev-context-menu
      ref="columnMenuRef"
      custom-class="modern-grid-header__column-menu"
      :items="columnMenuItems"
    />

    <!--  row menu  -->
    <ev-context-menu
      ref="rowMenuRef"
      :custom-class="`modern-grid-body__row-menu ${rowMenuInfo.customClass ?? ''}`"
      :items="rowMenuItems"
    />

    <!-- tooltip for chip-cell -->
    <base-custom-tooltip
      v-if="chipTooltipOptions === 'list'"
      ref="customTooltipRef"
      :use-deselect="true"
      :custom-class="(customClass?.chipCellTooltip || []).join(' ')"
    >
      <slot name="tooltipContent">
        <chip-cell-tooltip-content
          :items="tooltipItems"
          :field-name="currentTooltipFieldName as string"
          :row-data="currentTooltipRowData"
          :column-index="currentTooltipColumnIndex"
          :is-clickable="isChipCellClickable"
          @click-item="
            (event, fieldName, value, rowData, columnIndex) =>
              onClickTooltipItem({
                event,
                field: fieldName,
                value,
                row: rowData,
                cellIndex: columnIndex,
              } as ClickedCellInfoForTransformed<GridRowDataType<RowDataType>>)
          "
        />
      </slot>
    </base-custom-tooltip>
    <base-custom-tooltip
      v-else-if="chipTooltipOptions === 'chip'"
      ref="customTooltipRef"
      :use-deselect="true"
      :custom-class="`base-grid__cell-tooltip base-grid__cell-tooltip__chip ${(
        customClass?.chipCellTooltip || []
      ).join(' ')}`"
    >
      <slot name="tooltipContent">
        <tag-item
          v-for="(item, idx) in tooltipItems"
          :key="`tooltip-${idx}`"
          :title="item"
          size="xsmall"
        >
          {{ item }}
        </tag-item>
      </slot>
    </base-custom-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.modern-grid {
  display: flex;
  position: relative;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: visible;

  @include set-scrollbar();

  &-toolbar {
    flex: none;
    &-container {
      display: flex;
      flex-flow: row nowrap;
      gap: var(--grid-setting-margin);
      align-items: center;
      justify-content: space-between;
      height: var(--grid-toolbar-height);
      &--left {
        display: flex;
        flex: 1;
        flex-direction: column;
      }
      &--right {
        display: flex;
        flex: none;
        flex-direction: row;
        gap: var(--grid-setting-margin);
        align-items: center;
        justify-content: center;
        margin-left: auto;

        &.width-100 {
          width: 100%;
        }

        &:empty {
          display: none;
        }
      }
    }
    &__row {
      display: flex;
      align-items: center;
      height: var(--grid-toolbar-height);
      &.search.full {
        .ev-text-field {
          width: 100% !important;
        }
        .ev-input {
          height: var(--grid-toolbar-height) !important;
        }
      }
      &.search.right {
        margin-left: auto;
      }
      &.search.left {
        margin-right: auto;
      }
      &.custom.with-grid-setting {
        width: calc(100% - var(--grid-setting-width) - var(--grid-setting-margin));
      }
      &.align-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        .filter-search {
          flex: 1;
        }
      }
    }
  }

  &-sub-toolbar {
    margin-top: 12px;
  }

  &-header__expand-btn {
    &.ev-button:hover {
      background-color: transparent !important;
    }
    i {
      background-color: var(--tree-grid-collapse-icon-color) !important;
    }
  }

  &-body__expand-btn {
    &.ev-button:hover {
      background-color: transparent !important;
    }
    i {
      background-color: var(--tree-grid-collapse-icon-color) !important;
    }
  }

  &-body__expand-area {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
  }

  &__setting-btn,
  &-body__row-menu-btn {
    i {
      background-color: #adb5bd !important;
    }
  }

  &__setting-btn.ev-button {
    width: var(--grid-setting-width);
    height: var(--grid-setting-width) !important;

    i {
      background-color: var(--color-gray-02-10) !important;
    }

    &:hover {
      opacity: 1 !important;
      border-radius: 2px;
      background-color: var(--grid-setting-icon-hover-bg-color) !important;

      i {
        background-color: var(--grid-setting-icon-hover-color) !important;
      }
    }
  }

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: auto;
    border: 1px solid v-bind('styleInfo.borderColor ?? "var(--grid-border-color)"');
    background-color: v-bind('styleInfo.body?.backgroundColor ?? "var(--grid-body-bg-color)"');

    &:not(.no-header) {
      &::-webkit-scrollbar-button:vertical:start:decrement {
        display: block;
        height: v-bind('`${(styleInfo.header?.height ?? DEFAULT_HEADER_HEIGHT) * headerDepth}px`');
        background-color: v-bind(
          'styleInfo.header?.backgroundColor ?? "var(--grid-header-bg-color)"'
        );
      }

      &::-webkit-scrollbar-track {
        border-top: 1px solid v-bind('styleInfo.borderColor ?? "var(--grid-border-color)"');
        border-radius: 0 0 6px 6px !important;
      }
    }
  }

  &-body-area {
    position: absolute;
    left: 0;
    width: 100%;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  }

  &__wrapper {
    width: 100%;
    min-height: 0;

    &.no-use-filter {
      padding-top: 0;
    }
    &.none-border .modern-grid__container {
      border: none;
    }
    &.no-use-toolbar-and-pagination {
      height: 100%;
    }
    &.use-only-pagination {
      height: calc(100% - var(--grid-pagination-height));
    }
    &.use-only-one-toolbar {
      height: calc(100% - var(--grid-toolbar-height));
    }
    &.use-one-toolbar-and-pagination {
      height: calc(100% - var(--grid-toolbar-height) - var(--grid-pagination-height));
    }
    &.use-only-two-toolbar {
      height: calc(100% - var(--grid-toolbar-height) * 2);
    }
    &.use-two-toolbar-and-pagination {
      height: calc(100% - var(--grid-toolbar-height) * 2 - var(--grid-pagination-height));
    }
    &.use-summary {
      display: flex;
      flex-direction: column;
    }
  }

  &-table {
    display: grid;
    border-spacing: 0;
    border-collapse: collapse;
    &.is-dragging {
      cursor: grabbing;

      // 하위 요소들의 cursor도 grabbing으로 강제
      .modern-grid-header__cell,
      .modern-grid-header__cell-content,
      .modern-grid-header__cell-value__wrapper,
      .modern-grid-header__cell-value,
      .modern-grid-header__ordering-handle {
        cursor: grabbing !important;
      }

      // 드롭 불가 영역 위에 있을 때 no-drop 커서로 변경
      &.is-over-non-droppable {
        cursor: no-drop;

        .modern-grid-header__cell,
        .modern-grid-header__cell-content,
        .modern-grid-header__cell-value__wrapper,
        .modern-grid-header__cell-value,
        .modern-grid-header__ordering-handle {
          cursor: no-drop !important;
        }
      }
    }
  }

  &-header {
    display: grid;
    background-color: v-bind('styleInfo.header?.backgroundColor ?? "var(--grid-header-bg-color)"');
    &__resize-handle {
      position: absolute;
      top: 0;
      right: -2px;
      z-index: var(--header-control-handle-z-index);
      width: v-bind('resizeHandleWidth');
      height: v-bind('resizeHandleHeight ?? "100%"');
      opacity: 0;
      cursor: col-resize;
      user-select: none;
      background-color: v-bind(
        'columnResizingInfo.handleBarColor ?? "var(--grid-header-bg-color)"'
      );
      touch-action: none;
      &-mirror {
        display: none;
        position: absolute;
        top: 0;
        left: 0;
        z-index: var(--dragging-mirror-z-index);
        width: 1px;
        height: v-bind('cloneHeight + "px"');
        cursor: col-resize;
        background-color: v-bind(
          'columnResizingInfo.resizingHandleBarColor ?? "var(--grid-border-color)"'
        );
      }
      &-mirror.is-resizing {
        display: block;
      }
    }
    &__ordering-handle {
      cursor: grab !important;
    }
    &__row {
      display: flex;
      position: relative;
      z-index: var(--base-header-row-z-index);
      width: 100%;
    }
    &__cell {
      display: flex;
      position: relative;
      overflow: visible;
      outline: none;

      &.not-orderable.is-press-holding,
      &.not-orderable.is-press-holding * {
        cursor: not-allowed !important;
      }

      &-content {
        display: flex;
        position: absolute;
        bottom: 0;
        left: 0;
        align-items: center;
        width: 100%;
        background-color: v-bind(
          'styleInfo.header?.backgroundColor ?? "var(--grid-header-bg-color)"'
        );
        text-align: center;
      }
      &-value__wrapper {
        // block 컨테이너의 align-content는 Chrome 123+ 전용 → column flex로 세로 중앙 정렬 (Chrome 98 호환)
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: 0 8px;

        &--sortable {
          padding: 0 16px;
          cursor: pointer;
        }
      }
      &-value {
        display: inline-flex;
        width: auto !important;
        user-select: none;
        font-size: v-bind('styleInfo.header?.textSize');
        font-weight: v-bind('styleInfo.header?.textWeight');
        line-height: 1.5;
        color: v-bind('styleInfo.header?.textColor ?? "var(--main-font-color)"');
        text-align: center !important;

        @include shortening($line: 2);
      }
      &.left-align {
        .modern-grid-header__cell-content {
          text-align: start;
        }
      }
      &.center-align {
        .modern-grid-header__cell-content {
          text-align: center;
        }
      }
      &.is-resizing {
        isolation: isolate;
        will-change: width;
      }
      &.is-dragging-mirror {
        z-index: 10;
        opacity: 0.9;
      }
      &.is-drop-over {
        background-color: v-bind('columnOrderingInfo.dropElColor ?? "var(--grid-header-bg-color)"');
      }
      &.checkbox,
      &.expandBtn {
        :deep(.modern-grid-header__cell-content) {
          justify-content: center;
        }
      }
      &.is-sticky-column-left {
        background-color: v-bind(
          'styleInfo.header?.backgroundColor ?? "var(--grid-header-bg-color)"'
        );
        &.edge-sticky-left {
          border-right: 1px solid
            v-bind('styleInfo.header?.borderColor ?? "var(--grid-border-color)"');
        }
      }
      &.is-sticky-column-right {
        background-color: v-bind(
          'styleInfo.header?.backgroundColor ?? "var(--grid-header-bg-color)"'
        );
      }
      &:last-child .modern-grid-header__resize-handle {
        right: 0;
      }
    }
    &.show-column-line {
      .modern-grid-header__cell {
        border-left: 1px solid v-bind('styleInfo.header?.borderColor ?? "var(--grid-border-color)"');
        &:first-child {
          border-left: none;
        }
        &[data-is-group='true'] {
          border-bottom: 1px solid
            v-bind('styleInfo.header?.borderColor ?? "var(--grid-border-color)"');
        }
      }
    }
    &.show-row-line {
      .modern-grid-header__row {
        &:last-child {
          border-bottom: 1px solid
            v-bind('styleInfo.header?.borderColor ?? "var(--grid-border-color)"');
        }
        &:first-child {
          border-top: none;
        }
      }
    }
  }

  :deep(.modern-grid-header__cell) {
    .modern-grid-header__sorting-btn {
      display: none;
      position: absolute;
      right: 2px;
      z-index: var(--header-control-handle-z-index);
      min-width: 16px !important;
      height: 16px !important;
      padding: 0 !important;

      @include place-at-center('v');
      &:hover {
        background-color: transparent !important;
      }
    }
    .modern-grid-header__sorting-btn__icon {
      background-color: transparent !important;
      color: var(--grid-sort-icon-color) !important;

      &:active {
        background-color: transparent !important;
      }
    }
    .modern-grid-header__cell-value__wrapper:hover .modern-grid-header__sorting-btn,
    .modern-grid-header__sorting-btn.asc,
    .modern-grid-header__sorting-btn.desc {
      display: block;
    }
    .modern-grid-header__custom-header-sort {
      display: flex;
      flex: 1;
      align-items: center;
      height: 100%;
      overflow: hidden;

      &:hover .modern-grid-header__sorting-btn {
        display: block;
      }
    }
  }

  .is-sticky-column-left,
  .is-sticky-column-right {
    position: sticky;
    z-index: var(--sticky-column-z-index);
  }

  &-body {
    --dynamic-color: v-bind('styleInfo.body?.textColor ?? "var(--main-font-color)"');

    display: grid;
    position: relative;
    z-index: var(--base-cell-z-index);
    &__cell-value {
      width: auto !important;
      font-size: v-bind('styleInfo.body?.textSize');
      font-weight: v-bind('styleInfo.body?.textWeight');
      color: var(--dynamic-color);

      @include shortening();
    }
    &__row--selected .modern-grid-body__cell-value {
      color: var(--main-font-color);
    }
    &__row--disabled .modern-grid-body__cell-value {
      color: var(--grid-disabled-font-color);
    }
    &__cell {
      display: flex;
      flex: none;
      align-items: center;
      padding: 0 8px;
      div {
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      &-content {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      &--empty {
        width: v-bind('containerWidth');
        height: 50px;
        color: v-bind('styleInfo.body?.emptyColor ?? "var(--empty-color)"');
      }
      &--detail {
        width: 100%;
        padding: 0;
        .modern-grid-body__cell-content {
          align-items: start;
          height: 100%;
          overflow: auto;
        }
      }
      &--link {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        cursor: pointer;
        &:hover {
          text-decoration: underline var(--color-blue-05) !important;
        }

        &:empty {
          cursor: default;
          pointer-events: none;
        }

        .modern-grid-body__cell-value {
          color: var(--color-blue-05) !important;

          @include shortening(inline-block, auto);

          &--icon-right {
            max-width: calc(100% - 4px - 20px);
            margin-right: 4px;
          }
        }
      }
      &--row-menu {
        position: sticky;
        right: 0;
        z-index: var(--sticky-column-z-index + 1);
        flex: 1;
        width: 0;
      }
      &.checkbox,
      &.expandBtn {
        .modern-grid-body__cell-content {
          justify-content: center;
        }
      }
      &.left-align .modern-grid-body__cell-content {
        justify-content: start;
      }
      &.center-align .modern-grid-body__cell-content {
        justify-content: center;
      }
      &.right-align .modern-grid-body__cell-content {
        justify-content: end;
      }

      /* 링크 셀도 정렬 옵션을 따르도록 처리 */
      &.left-align .modern-grid-body__cell--link {
        justify-content: start;
        text-align: left;
      }
      &.center-align .modern-grid-body__cell--link {
        justify-content: center;
        text-align: center;
      }
      &.right-align .modern-grid-body__cell--link {
        justify-content: end;
        text-align: right;
      }
      &.is-resizing {
        isolation: isolate;
        will-change: width;
      }
      &.is-sticky-column-left {
        background-color: v-bind('styleInfo.body?.backgroundColor ?? "var(--grid-body-bg-color)"');
        &.edge-sticky-left {
          border-right: 1px solid
            v-bind('styleInfo.body?.borderColor ?? "var(--grid-border-color)"');
        }
      }
      &.is-sticky-column-right {
        background-color: v-bind('styleInfo.body?.backgroundColor ?? "var(--grid-body-bg-color)"');
      }
      &--changed {
        background-color: v-bind('styleInfo.body?.changedCellColor ?? "var(--color-blue-09-01)"');
      }
      :deep(.wide-dropdown-select__select-trigger-wrap) {
        display: flex;
        align-items: center;
      }
      :deep(.wide-dropdown-select__select-trigger) {
        height: 20px;
      }
    }
    &__row {
      display: flex;
      position: absolute;
      z-index: var(--base-body-row-z-index);
      width: 100%;
      &--highlighted {
        background-color: v-bind('styleInfo.body?.highlightedColor ?? "transparent"');
        .is-sticky-column-left,
        .is-sticky-column-right {
          background-color: v-bind('styleInfo.body?.highlightedColor ?? "transparent"');
        }
      }
      &--selected {
        background-color: v-bind(
          'styleInfo.body?.selectedColor ?? "var(--grid-selected-bg-color)"'
        );
        .is-sticky-column-left,
        .is-sticky-column-right {
          background-color: v-bind(
            'styleInfo.body?.selectedColor ?? "var(--grid-selected-bg-color)"'
          );
        }
        .ratio-text {
          color: var(--main-font-color);
        }
        .base-grid__cell--link,
        .modern-grid-body__cell--link {
          color: var(--grid-selected-link-color) !important;
        }
      }
      &--disabled {
        background-color: var(--grid-disabled-bg-color);
        .is-sticky-column-left,
        .is-sticky-column-right {
          background-color: var(--grid-disabled-bg-color);
        }
      }
      &-menu-btn {
        display: none;
        position: absolute;
        right: 6px;
        z-index: var(--header-control-handle-z-index);
        cursor: pointer;

        @include place-at-center('v');
        &.ev-button:hover {
          background-color: var(--grid-setting-icon-hover-bg-color) !important;
          i {
            background-color: var(--grid-setting-icon-hover-color) !important;
          }
        }
      }
      &:not(&--empty):hover {
        background-color: v-bind(
          'styleInfo.body?.hoverColor ?? "var(--grid-hover-bg-color)"'
        ) !important;
        .modern-grid-body__cell {
          border-color: v-bind(
            'styleInfo.body?.hoverBorderColor ?? "var(--grid-hover-border-right-color)"'
          ) !important;
        }
        .is-sticky-column-left,
        .is-sticky-column-right {
          background-color: v-bind(
            'styleInfo.body?.hoverColor ?? "var(--grid-hover-bg-color)"'
          ) !important;
        }
        .modern-grid-body__row-menu-btn {
          display: block;
        }
      }
    }
    .base-grid__ratio {
      justify-content: center;
      width: 100%;
      max-width: 120px;
    }
    .label-cell-wrapper {
      width: 100%;
      text-align: center;
    }
    &.show-column-line {
      .modern-grid-body__cell {
        border-left: 1px solid v-bind('styleInfo.body?.borderColor ?? "var(--grid-border-color)"');
        &:first-child {
          border-left: none;
        }
      }
    }
    &.show-row-line {
      .modern-grid-body__row {
        border-bottom: 1px solid v-bind('styleInfo.body?.borderColor ?? "var(--grid-border-color)"');
        &--empty {
          border-bottom: none;
        }
      }
    }
    &.is-striped {
      .modern-grid-body__row:nth-child(odd) .modern-grid-body__cell {
        background-color: v-bind('styleInfo.body?.stripedColor ?? "transparent"');
      }
      .modern-grid-body__row:nth-child(even) .modern-grid-body__cell {
        background-color: v-bind('styleInfo.body?.backgroundColor ?? "var(--grid-body-bg-color)"');
      }
    }
  }

  &-table.is-sticky-header .modern-grid-header {
    position: sticky;
    top: 0;
    left: 0;
    z-index: var(--sticky-header-z-index);
  }

  &-table.is-dragging .modern-grid-body {
    user-select: none;
  }

  &__pagination {
    flex: none;
    height: var(--grid-pagination-height);
    padding: 4px 0;
  }

  .ev-icon-square-plus {
    mask: url('@/common/assets/sparrow/icon-sparrow-plus-square.svg') center center no-repeat;
    font-size: 12px !important;
  }

  .ev-icon-square-minus {
    mask: url('@/common/assets/sparrow/icon-sparrow-minus-square.svg') center center no-repeat;
    font-size: 12px !important;
  }

  .ev-icon-s-arrow-down {
    mask: url('@/common/assets/sparrow/icon-sparrow-arrow-down.svg') center center no-repeat;
  }

  .ev-icon-s-arrow-right {
    mask: url('@/common/assets/sparrow/icon-sparrow-arrow-right.svg') center center no-repeat;
  }

  :deep(.tag-item) {
    max-width: 100%;
    height: 16px;
    margin-right: 4px;
    line-height: 16px;

    > span {
      @include shortening();
    }
  }

  &-summary {
    flex-shrink: 0;
    overflow: hidden;
    border: 1px solid v-bind('styleInfo.borderColor ?? "var(--grid-border-color)"');
    border-top: none;
    background-color: v-bind('styleInfo.body?.selectedColor ?? "var(--grid-selected-bg-color)"');

    &__row {
      display: flex;
    }

    &__cell {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      min-height: 36px;
      padding: 0 8px;
      border-right: 1px solid var(--grid-line-color);
      font-weight: 600;

      &:last-child {
        border-right: none;
      }

      &.left-align {
        justify-content: flex-start;
      }

      &.center-align {
        justify-content: center;
      }

      &.right-align {
        justify-content: flex-end;
      }
    }

    &__cell-content {
      overflow: hidden;
      font-size: 12px;
      color: var(--color-blue-05);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.sum-avg-toggle--sum {
    .modern-grid-header__cell.is-toggled .column-name {
      color: var(--grid-toggle-sum-color);
    }
  }

  &.sum-avg-toggle--avg {
    .modern-grid-header__cell.is-toggled .column-name {
      color: var(--grid-toggle-avg-color);
    }
  }
}
</style>
